import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Trophy, X, Check, RotateCcw, Sparkles, Brain, ChevronRight, Home, Moon } from 'lucide-react';

// Confetti function
const triggerConfetti = () => {
  try {
    import('canvas-confetti').then((confettiModule) => {
      const confetti = confettiModule.default;
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#8B5CF6', '#A78BFA', '#7C3AED']
      });
    });
  } catch (e) {
    console.log('Confetti not available');
  }
};

// Quiz Questions sur le Tarot
const QUIZ_QUESTIONS = [
  {
    id: 1,
    category: "Les Arcanes Majeurs",
    question: "Combien y a-t-il d'Arcanes Majeurs dans le Tarot de Marseille ?",
    options: [
      "21 cartes",
      "22 cartes",
      "56 cartes",
      "78 cartes"
    ],
    correctAnswer: 1,
    explanation: "Le Tarot de Marseille compte 22 Arcanes Majeurs, numérotés de 0 (Le Mat) à 21 (Le Monde). Ces cartes représentent le voyage archétypal de l'âme."
  },
  {
    id: 2,
    category: "Le Mat",
    question: "Quel est le numéro de la carte Le Mat ?",
    options: [
      "1",
      "21",
      "0",
      "22"
    ],
    correctAnswer: 2,
    explanation: "Le Mat porte le numéro 0. Il représente le commencement, l'innocence et le potentiel infini. C'est la seule carte sans numéro fixe dans certaines traditions."
  },
  {
    id: 3,
    category: "Symbolisme",
    question: "Que représente symboliquement La Lune (XVIII) ?",
    options: [
      "La clarté et la vérité",
      "L'inconscient, les illusions et l'intuition",
      "L'amour romantique",
      "La richesse matérielle"
    ],
    correctAnswer: 1,
    explanation: "La Lune symbolise l'inconscient, les rêves, les illusions et l'intuition profonde. Elle invite à explorer notre monde intérieur et nos peurs cachées."
  },
  {
    id: 4,
    category: "La Roue de Fortune",
    question: "Que symbolise La Roue de Fortune (X) ?",
    options: [
      "La richesse garantie",
      "Les cycles de la vie et le changement",
      "Le jeu et les paris",
      "La chance constante"
    ],
    correctAnswer: 1,
    explanation: "La Roue de Fortune représente les cycles de la vie, le changement inévitable et les tournants du destin. Elle rappelle que tout est en mouvement perpétuel."
  },
  {
    id: 5,
    category: "L'Ermite",
    question: "Quel objet L'Ermite (IX) tient-il généralement dans sa main ?",
    options: [
      "Une épée",
      "Une lanterne",
      "Un sceptre",
      "Un livre"
    ],
    correctAnswer: 1,
    explanation: "L'Ermite porte une lanterne qui symbolise la lumière intérieure, la sagesse et la recherche de la vérité. Il représente l'introspection et la guidance spirituelle."
  },
  {
    id: 6,
    category: "Les Éléments",
    question: "À quel élément est généralement associé L'Empereur (IV) ?",
    options: [
      "L'Eau",
      "Le Feu",
      "L'Air",
      "La Terre"
    ],
    correctAnswer: 1,
    explanation: "L'Empereur est associé au Feu, symbolisant l'action, l'autorité, la volonté et le leadership. C'est une énergie masculine active et structurante."
  },
  {
    id: 7,
    category: "La Justice",
    question: "Que tient La Justice (VIII ou XI selon les traditions) dans ses mains ?",
    options: [
      "Un livre et une plume",
      "Une balance et une épée",
      "Un sceptre et un globe",
      "Une fleur et un miroir"
    ],
    correctAnswer: 1,
    explanation: "La Justice tient une balance (équité, équilibre) et une épée (vérité, discernement). Elle représente l'objectivité, les conséquences et l'équilibre karmique."
  },
  {
    id: 8,
    category: "L'Impératrice",
    question: "L'Impératrice (III) est principalement associée à :",
    options: [
      "La guerre et le conflit",
      "La fertilité, la créativité et l'abondance",
      "La solitude et l'ermitage",
      "La mort et la transformation"
    ],
    correctAnswer: 1,
    explanation: "L'Impératrice représente la fertilité, la créativité, l'abondance et l'énergie féminine. Elle symbolise aussi la nature, la beauté et la maternité."
  },
  {
    id: 9,
    category: "La Mort",
    question: "Que représente réellement la carte de La Mort (XIII) ?",
    options: [
      "La mort physique uniquement",
      "La transformation, la fin d'un cycle et le renouveau",
      "La maladie à venir",
      "Un mauvais présage"
    ],
    correctAnswer: 1,
    explanation: "La Mort symbolise la transformation profonde, la fin d'un cycle et le renouveau. Elle n'annonce pas une mort physique mais une renaissance symbolique."
  },
  {
    id: 10,
    category: "Le Monde",
    question: "Le Monde (XXI) est la dernière carte numérotée. Que représente-t-elle ?",
    options: [
      "La fin de tout",
      "L'accomplissement, la réalisation et l'unité",
      "Les voyages lointains",
      "La domination mondiale"
    ],
    correctAnswer: 1,
    explanation: "Le Monde représente l'accomplissement, la réalisation de soi et l'unité avec l'univers. C'est l'aboutissement positif du voyage des Arcanes Majeurs."
  },
  {
    id: 11,
    category: "Les Amoureux",
    question: "Que symbolise principalement la carte Les Amoureux (VI) ?",
    options: [
      "Uniquement l'amour romantique",
      "Les choix, les dilemmes et les unions",
      "Le mariage obligatoire",
      "Les conflits familiaux"
    ],
    correctAnswer: 1,
    explanation: "Les Amoureux représentent les choix importants, les dilemmes moraux et les unions (pas seulement romantiques). C'est la carte des décisions cruciales."
  },
  {
    id: 12,
    category: "Le Pendu",
    question: "Le Pendu (XII) est suspendu par :",
    options: [
      "Le cou",
      "Les mains",
      "Un pied",
      "La taille"
    ],
    correctAnswer: 2,
    explanation: "Le Pendu est suspendu par un pied, suggérant un changement de perspective volontaire, le sacrifice et la suspension du temps pour mieux voir."
  },
  {
    id: 13,
    category: "La Tour",
    question: "Que représente La Tour (XVI) foudroyée ?",
    options: [
      "La construction d'un nouveau bâtiment",
      "L'effondrement soudain des certitudes et la révélation",
      "Un investissement immobilier",
      "La victoire militaire"
    ],
    correctAnswer: 1,
    explanation: "La Tour représente l'effondrement brutal des structures, des illusions et des certitudes. Elle annonce une révélation parfois douloureuse mais libératrice."
  },
  {
    id: 14,
    category: "L'Étoile",
    question: "L'Étoile (XVII) représente principalement :",
    options: [
      "La célébrité",
      "L'espoir, l'inspiration et la sérénité",
      "L'astronomie",
      "Les souhaits matériels"
    ],
    correctAnswer: 1,
    explanation: "L'Étoile symbolise l'espoir, l'inspiration, la sérénité et la connexion cosmique. Elle apporte un message de guérison et de renouveau après la tempête."
  },
  {
    id: 15,
    category: "Le Chariot",
    question: "Que symbolise Le Chariot (VII) ?",
    options: [
      "Les voyages en voiture",
      "La victoire, la maîtrise et l'avancée déterminée",
      "La fuite des problèmes",
      "La compétition sportive"
    ],
    correctAnswer: 1,
    explanation: "Le Chariot représente la victoire par la maîtrise de soi, la détermination et l'avancée résolue. Il symbolise le triomphe sur les obstacles intérieurs."
  },
  {
    id: 16,
    category: "Tirages",
    question: "Dans un tirage en croix celtique, combien de cartes utilise-t-on traditionnellement ?",
    options: [
      "3 cartes",
      "7 cartes",
      "10 cartes",
      "22 cartes"
    ],
    correctAnswer: 2,
    explanation: "La Croix Celtique utilise traditionnellement 10 cartes, couvrant le présent, les influences, le passé, le futur proche, le conscient, l'inconscient, et plus."
  },
  {
    id: 17,
    category: "Cartes renversées",
    question: "Que peut signifier une carte tirée à l'envers (renversée) ?",
    options: [
      "Toujours quelque chose de négatif",
      "Une énergie bloquée, intériorisée ou à développer",
      "Que le tirage est invalide",
      "Qu'il faut retirer une autre carte"
    ],
    correctAnswer: 1,
    explanation: "Une carte renversée peut indiquer une énergie bloquée, intériorisée, en développement ou qui nécessite attention. Ce n'est pas forcément négatif."
  },
  {
    id: 18,
    category: "Histoire",
    question: "D'où vient le nom 'Tarot de Marseille' ?",
    options: [
      "Il a été créé à Marseille",
      "C'est le style de dessin popularisé par les cartiers marseillais au XVIIIe siècle",
      "C'est le nom de l'inventeur",
      "Il a été trouvé à Marseille"
    ],
    correctAnswer: 1,
    explanation: "Le nom vient des cartiers marseillais qui ont standardisé et popularisé ce style de Tarot au XVIIIe siècle, bien que son origine soit plus ancienne."
  },
  {
    id: 19,
    category: "Le Diable",
    question: "Que représente Le Diable (XV) dans une lecture positive ?",
    options: [
      "Le mal absolu",
      "Les liens, les passions et les forces terrestres à maîtriser",
      "La punition divine",
      "L'échec certain"
    ],
    correctAnswer: 1,
    explanation: "Le Diable représente les attachements, les passions, les forces instinctives et matérielles. Il invite à reconnaître et maîtriser nos chaînes, pas à les craindre."
  },
  {
    id: 20,
    category: "Le Soleil",
    question: "Le Soleil (XIX) est généralement considéré comme :",
    options: [
      "Une carte neutre",
      "Une des cartes les plus positives du Tarot",
      "Une carte d'avertissement",
      "Une carte uniquement estivale"
    ],
    correctAnswer: 1,
    explanation: "Le Soleil est l'une des cartes les plus positives, symbolisant la joie, le succès, la vitalité, la clarté et l'épanouissement. C'est une carte de lumière."
  }
];

const MysteriaeQuizPage = () => {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);

  const question = QUIZ_QUESTIONS[currentQuestion];
  const progress = ((currentQuestion + 1) / QUIZ_QUESTIONS.length) * 100;

  const handleAnswerSelect = (index) => {
    if (showExplanation) return;
    setSelectedAnswer(index);
  };

  const handleValidate = () => {
    if (selectedAnswer === null) return;

    const isCorrect = selectedAnswer === question.correctAnswer;
    
    setAnswers([...answers, {
      questionId: question.id,
      selected: selectedAnswer,
      correct: question.correctAnswer,
      isCorrect
    }]);

    if (isCorrect) {
      setScore(score + 1);
    }

    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentQuestion < QUIZ_QUESTIONS.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setQuizCompleted(true);
      if (score >= QUIZ_QUESTIONS.length * 0.7) {
        triggerConfetti();
      }
    }
  };

  const restartQuiz = () => {
    setCurrentQuestion(0);
    setSelectedAnswer(null);
    setScore(0);
    setAnswers([]);
    setQuizCompleted(false);
    setShowExplanation(false);
  };

  const getScoreMessage = () => {
    const percentage = (score / QUIZ_QUESTIONS.length) * 100;
    if (percentage >= 90) return { title: "Excellent !", message: "Vous êtes un véritable initié du Tarot !", emoji: "🏆" };
    if (percentage >= 70) return { title: "Très bien !", message: "Vous maîtrisez les bases des Arcanes Majeurs !", emoji: "🌟" };
    if (percentage >= 50) return { title: "Bien !", message: "Continuez à explorer les mystères du Tarot.", emoji: "👍" };
    return { title: "Courage !", message: "Découvrez la section 'Découvrir' pour progresser.", emoji: "📚" };
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Les Arcanes Majeurs": "bg-purple-500",
      "Le Mat": "bg-indigo-500",
      "Symbolisme": "bg-violet-500",
      "La Roue de Fortune": "bg-amber-500",
      "L'Ermite": "bg-slate-500",
      "Les Éléments": "bg-red-500",
      "La Justice": "bg-blue-500",
      "L'Impératrice": "bg-emerald-500",
      "La Mort": "bg-gray-600",
      "Le Monde": "bg-cyan-500",
      "Les Amoureux": "bg-pink-500",
      "Le Pendu": "bg-teal-500",
      "La Tour": "bg-orange-500",
      "L'Étoile": "bg-sky-500",
      "Le Chariot": "bg-yellow-500",
      "Tirages": "bg-purple-600",
      "Cartes renversées": "bg-violet-600",
      "Histoire": "bg-amber-600",
      "Le Diable": "bg-rose-600",
      "Le Soleil": "bg-yellow-400"
    };
    return colors[category] || "bg-gray-500";
  };

  if (quizCompleted) {
    const scoreInfo = getScoreMessage();
    
    return (
      <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 py-12 px-4 md:px-8">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl border border-purple-200 shadow-lg p-8 text-center"
          >
            <div className="text-6xl mb-6">{scoreInfo.emoji}</div>
            <h1 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">{scoreInfo.title}</h1>
            <p className="text-purple-700 text-lg mb-8">{scoreInfo.message}</p>
            
            <div className="bg-purple-50 rounded-2xl p-6 mb-8 border border-purple-200">
              <div className="flex items-center justify-center gap-4 mb-4">
                <Trophy className="h-8 w-8 text-amber-500" />
                <span className="text-4xl font-bold text-purple-700">{score}/{QUIZ_QUESTIONS.length}</span>
              </div>
              <p className="text-sm text-purple-600">
                {Math.round((score / QUIZ_QUESTIONS.length) * 100)}% de bonnes réponses
              </p>
            </div>

            {/* Detailed Results */}
            <div className="text-left mb-8">
              <h3 className="font-semibold mb-4 text-gray-800">Détail par catégorie :</h3>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {Object.entries(
                  answers.reduce((acc, answer) => {
                    const q = QUIZ_QUESTIONS.find(q => q.id === answer.questionId);
                    if (!acc[q.category]) acc[q.category] = { correct: 0, total: 0 };
                    acc[q.category].total++;
                    if (answer.isCorrect) acc[q.category].correct++;
                    return acc;
                  }, {})
                ).map(([category, stats]) => (
                  <div key={category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <span className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`}></span>
                      {category}
                    </span>
                    <span className={stats.correct === stats.total ? "text-purple-600 font-medium" : "text-gray-500"}>
                      {stats.correct}/{stats.total}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={restartQuiz}
                className="flex-1 rounded-full bg-purple-600 hover:bg-purple-500"
                data-testid="restart-quiz-btn"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Recommencer
              </Button>
              <Button
                onClick={() => navigate('/mysteriae/knowledge')}
                variant="outline"
                className="flex-1 rounded-full border-purple-300 text-purple-700 hover:bg-purple-50"
              >
                <Brain className="h-4 w-4 mr-2" />
                Découvrir
              </Button>
              <Button
                onClick={() => navigate('/mysteriae')}
                variant="ghost"
                className="flex-1 rounded-full text-gray-600 hover:bg-gray-100"
              >
                <Home className="h-4 w-4 mr-2" />
                Accueil
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-violet-50 via-white to-purple-50 py-12 px-4 md:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/mysteriae')}
            className="mb-6 rounded-full text-gray-600 hover:bg-purple-50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
                <Moon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Quiz Tarot</h1>
                <p className="text-sm text-purple-600">Testez vos connaissances des Arcanes</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold text-purple-600">{currentQuestion + 1}/{QUIZ_QUESTIONS.length}</p>
              <p className="text-xs text-gray-500">Score: {score}</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 bg-purple-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="bg-white rounded-3xl border border-purple-200 shadow-lg overflow-hidden"
          >
            {/* Category Badge */}
            <div className={`px-6 py-3 ${getCategoryColor(question.category)}`}>
              <span className="text-white text-sm font-medium">{question.category}</span>
            </div>

            <div className="p-6 md:p-8">
              {/* Question */}
              <h2 className="text-xl md:text-2xl mb-8 leading-relaxed text-gray-900 font-semibold">
                {question.question}
              </h2>

              {/* Options */}
              <div className="space-y-3 mb-8">
                {question.options.map((option, index) => {
                  const isSelected = selectedAnswer === index;
                  const isCorrect = index === question.correctAnswer;
                  const showCorrectness = showExplanation;

                  let bgClass = "bg-gray-50 hover:bg-purple-50 border-gray-200";
                  let iconClass = null;

                  if (showCorrectness) {
                    if (isCorrect) {
                      bgClass = "bg-purple-50 border-purple-500";
                      iconClass = <Check className="h-5 w-5 text-purple-600" />;
                    } else if (isSelected && !isCorrect) {
                      bgClass = "bg-red-50 border-red-400";
                      iconClass = <X className="h-5 w-5 text-red-500" />;
                    }
                  } else if (isSelected) {
                    bgClass = "bg-purple-100 border-purple-400";
                  }

                  return (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={showExplanation}
                      className={`w-full p-4 rounded-xl border-2 text-left transition-all flex items-center justify-between ${bgClass} ${
                        showExplanation ? 'cursor-default' : 'cursor-pointer'
                      }`}
                      data-testid={`option-${index}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          isSelected && !showCorrectness ? 'bg-purple-500 text-white' : 'bg-gray-200 text-gray-600'
                        }`}>
                          {String.fromCharCode(65 + index)}
                        </span>
                        <span className="text-sm md:text-base text-gray-700">{option}</span>
                      </div>
                      {iconClass}
                    </button>
                  );
                })}
              </div>

              {/* Explanation */}
              <AnimatePresence>
                {showExplanation && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mb-6"
                  >
                    <div className={`p-4 rounded-xl ${
                      selectedAnswer === question.correctAnswer 
                        ? 'bg-purple-50 border border-purple-300' 
                        : 'bg-amber-50 border border-amber-300'
                    }`}>
                      <div className="flex items-start gap-3">
                        <Sparkles className={`h-5 w-5 mt-0.5 ${
                          selectedAnswer === question.correctAnswer 
                            ? 'text-purple-600' 
                            : 'text-amber-600'
                        }`} />
                        <div>
                          <p className={`font-medium mb-1 ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-purple-700' 
                              : 'text-amber-700'
                          }`}>
                            {selectedAnswer === question.correctAnswer ? 'Correct !' : 'Explication'}
                          </p>
                          <p className={`text-sm ${
                            selectedAnswer === question.correctAnswer 
                              ? 'text-purple-600' 
                              : 'text-amber-600'
                          }`}>
                            {question.explanation}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Actions */}
              <div className="flex justify-end">
                {!showExplanation ? (
                  <Button
                    onClick={handleValidate}
                    disabled={selectedAnswer === null}
                    className="rounded-full bg-purple-600 hover:bg-purple-500 px-8"
                    data-testid="validate-btn"
                  >
                    Valider
                  </Button>
                ) : (
                  <Button
                    onClick={handleNext}
                    className="rounded-full bg-purple-600 hover:bg-purple-500 px-8"
                    data-testid="next-btn"
                  >
                    {currentQuestion < QUIZ_QUESTIONS.length - 1 ? (
                      <>
                        Question suivante
                        <ChevronRight className="h-4 w-4 ml-2" />
                      </>
                    ) : (
                      <>
                        Voir mes résultats
                        <Trophy className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default MysteriaeQuizPage;
